import { useEffect, useState } from "react";
import {
  useAlertStore,
  usePopupStore,
  useThemeStore,
  useUserStore,
} from "../../../stores";
import { CustomerDetailsProps } from "./types";
import { AlignItems, JustifyContent, Wrap } from "../../containers/types";
import { Column, Row, ScrollRow } from "../../containers";
import { PopupBack, PopupClose, QuestionBox } from "../../../popup";
import { Heading } from "../../typography";
import { TextInput } from "../../inputs";
import { Tags } from "../../tag";
import OptionList from "../../optionlist";
import { Vec2 } from "../../../types";
import { EditIcon, TrashIcon } from "../../../assets/icons";
import CustomerEditPopup from "../../../pages/customers/customer-edit-popup";
import { CustomerApi } from "../../../api";
import ActivityRenderer from "../../activity";
import { ActivityRendererType } from "../../activity/types";

const CustomerDetails = <T extends {}>(props: CustomerDetailsProps<T>) => {
  const { customer, back } = props;

  const theme = useThemeStore((s) => s.theme);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);
  const hasUserAccess = useUserStore((s) => s.hasUserAccess);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
        maxWidth: 816,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const onEdit = (): void => {
    setPopupBody(
      <CustomerEditPopup<CustomerDetailsProps<T>>
        {...customer}
        back={{ component: CustomerDetails, props }}
      />
    );
  };

  const onDelete = (): void => {
    setPopupBody(
      <QuestionBox<CustomerDetailsProps<T>>
        question={`آیا مطمئن هستید که می خواهید مشتری ${customer.fullname} را حذف کنید؟`}
        close={false}
        back={{ component: CustomerDetails, props }}
        onNo={() => {
          setPopupBody(<CustomerDetails {...props} />);
        }}
        onYes={() => {
          if (!hasUserAccess(["AddEditCustomer"]))
            return pushAlert("دسترسی غیر مجاز", "error");
          CustomerApi.deleteCustomer(customer)
            .then(() => {
              setPopupBody(back ? <back.component {...back.props} /> : null);
              pushAlert("عملیات موفقیت آمیز");
            })
            .catch((err) => pushAlert(err.msg || "unknown error", "error"));
        }}
      />
    );
  };

  return (
    <Column
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Start}
      style={{ marginBottom: "1rem", position: "relative" }}
      gap={20}
      props={{ onContextMenu: onRightClick }}
    >
      <PopupClose />

      {back ? (
        <PopupBack Component={back.component} props={back.props} />
      ) : (
        <></>
      )}

      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "ویرایش",
            Icon: EditIcon,
            onClick: onEdit,
          },
          {
            title: "حذف",
            Icon: TrashIcon,
            color: theme.alert,
            iconColor: theme.alert,
            onClick: onDelete,
          },
        ]}
      />

      <Heading
        textStyles={{ marginTop: "1rem" }}
        size={19}
        color={theme.textOnOverlay}
      >
        اطلاعات مشتری
      </Heading>
      <Row
        style={{ width: "90%" }}
        justifyContent={JustifyContent.Between}
        gap={20}
        flexWrap={Wrap.Wrap}
        className="customer-activity-popup-row"
      >
        <TextInput
          style={{ maxWidth: 300 }}
          label="نام و نام خانوادگی"
          value={customer.fullname}
          inputProps={{ readOnly: true }}
        />
        <TextInput
          style={{ maxWidth: 300 }}
          label="کد ملی"
          value={customer.national_code}
          inputProps={{ readOnly: true }}
        />
      </Row>
      <Row
        style={{ width: "90%" }}
        justifyContent={JustifyContent.Between}
        gap={20}
        flexWrap={Wrap.Wrap}
        className="customer-activity-popup-row"
      >
        <TextInput
          style={{ maxWidth: 300 }}
          label="تلفن همراه"
          value={customer.phone}
          inputProps={{ readOnly: true }}
        />
        <TextInput
          style={{ maxWidth: 300 }}
          label="آدرس"
          value={customer.address}
          inputProps={{ readOnly: true }}
        />
      </Row>
      <Row
        style={{ width: "90%" }}
        justifyContent={JustifyContent.Between}
        gap={20}
        flexWrap={Wrap.Wrap}
      >
        <Tags>{customer.tags}</Tags>
      </Row>

      <Column style={{ width: "100%" }}>
        <Heading
          size={18}
          textStyles={{ width: "90%", textAlign: "right" }}
          color={theme.textOnOverlay}
        >
          {`رویداد های ${customer.fullname}`}
        </Heading>
        <Row style={{ width: "90%" }}>
          <ScrollRow gap={20} height={300}>
            {customer.activities.map((activity, idx) => (
              <ActivityRenderer<CustomerDetailsProps<T>>
                key={idx}
                activity={activity}
                type={ActivityRendererType.Small}
                back={{
                  component: CustomerDetails,
                  props: { ...props, ...customer },
                }}
              />
            ))}
          </ScrollRow>
        </Row>
      </Column>
    </Column>
  );
};

export default CustomerDetails;
